import { LoadingOverlayComponent } from '../../../shared/components/loading-overlay/loading-overlay.component';
import { DialogCancelUserComponent } from '../dialog-cancel-user/dialog-cancel-user.component';
import { DialogRemoveUserComponent } from '../dialog-remove-user/dialog-remove-user.component';
import { DialogSummarizeUserComponent } from '../dialog-summarize-user/dialog-summarize-user.component';
import { UsersTabAppComponent } from './components/users-tab-app/users-tab-app.component';
import { UsersTabShippingComponent } from './components/users-tab-shipping/users-tab-shipping.component';
import { UsersTabWebsiteComponent } from './components/users-tab-website/users-tab-website.component';

import { AuthService } from '../../../auth/auth.service';
import { UserService } from '../../services/user.service';
import {
  DeliveryTargets,
  DeliveryTargetType,
  UserAdminDeliveryTargetService,
} from '../../services/user-admin-delivery-target.service';
import {
  UploadedUser,
  UserAdminImportService,
} from '../../services/user-admin-import.service';
import { UserAdminUserProductService } from '../../services/user-admin-user-product.service';

import { PhoneNumberPipe } from '../../../shared/phoneNumber.pipe';
import { IsNotEmptyObjectPipe } from '../../../shared/pipes/is-not-empty-object.pipe';
import { UserStatusIconPipe } from '../../pipes/user-status-icon.pipe';
import { HasUserAccessPipe } from './pipes/has-user-access.pipe';
import { IsUserAdminPipe } from './pipes/is-user-admin.pipe';
import { IsUserAllowedToAccessAllProductsPipe } from './pipes/is-user-allowed-to-access-all-products.pipe';
import { UserActiveProductIdsPipe } from './pipes/user-active-product-ids.pipe';

import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  SimpleChanges,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { DateValidator } from '../../../shared/date.validator';
import { EndDateValidator } from '../../../shared/endDate.validator';
import { UsernameValidator } from '../../../shared/username.validator';
import { verifyPhoneNumber } from '../../constants/constants';
import { EgpTypes } from '../../models/egp-types.interface';
import { UserStationChange } from '../../models/user-station-change.interface';
import { UsersTabMasterDataComponent } from './components/users-tab-master-data/users-tab-master-data.componen';
import { AddEvent } from './models/add-event.interface';
import { ClickSectionCheckbox } from './models/click-section-checkbox.interface';
import { RemoveEvent } from './models/remove-event.interface';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { isNil } from 'src/utils/utils';

interface ValidatorFn {
  (c: AbstractControl): ValidationErrors | null;
}

export const MODULES = [
  CommonModule,
  MatCardModule,
  MatButtonModule,
  MatFormFieldModule,
  MatInputModule,
  ReactiveFormsModule,
  FormsModule,
  MatIconModule,
  MatTooltipModule,
  MatPaginatorModule,
  MatStepperModule,
  MatChipsModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaIconComponent,
  UsersTabMasterDataComponent,
  UsersTabWebsiteComponent,
  UsersTabShippingComponent,
  UsersTabAppComponent,
];
export const PIPES = [
  PhoneNumberPipe,
  IsNotEmptyObjectPipe,
  IsUserAdminPipe,
  HasUserAccessPipe,
  IsUserAllowedToAccessAllProductsPipe,
  UserActiveProductIdsPipe,
  UserStatusIconPipe,
];

@Component({
  selector: 'app-user-admin-users-tab',
  templateUrl: './user-admin-users-tab.component.html',
  styleUrls: ['../../user-admin.component.scss'],
  providers: [],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, LoadingOverlayComponent],
})
export class UserAdminUsersTabComponent
  implements OnInit, OnDestroy, OnChanges
{
  private auth = inject(AuthService);
  private _snackBar = inject(MatSnackBar);
  public userService = inject(UserService);
  public dialog = inject(MatDialog);
  private _formBuilder = inject(UntypedFormBuilder);
  private cd = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private injector = inject(Injector);
  private readonly userAdminImportService = inject(UserAdminImportService);
  private readonly deliveryTargetService = inject(
    UserAdminDeliveryTargetService
  );
  private readonly userProductService = inject(UserAdminUserProductService);

  productList = input<any>();
  contingents = input<any>();
  searchString = model<string>(undefined); // needed to filter table in combination with searchstring.pipe

  usersTabWebsiteComponent = viewChild<UsersTabWebsiteComponent>(
    UsersTabWebsiteComponent
  );
  usersTabShippingComponent = viewChild<UsersTabShippingComponent>(
    UsersTabShippingComponent
  );
  usersTabAppComponent = viewChild<UsersTabAppComponent>(UsersTabAppComponent);

  fileInput = viewChild<ElementRef>('fileInput');

  visible = true;
  selectable = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  deliveryTargetIcons = {
    email: ['far', 'at'],
    sms: ['far', 'sms'],
    fax: ['far', 'fax'],
  };

  deliveryTargets = signal<DeliveryTargets>({
    email: [],
    fax: [],
    sms: [],
  });

  egwgmaSmsPause = {
    isPause: false,
    start: 22,
    end: 6,
  };

  egwsummerConfig = {
    smsPause: {
      isPause: false,
      start: 22,
      end: 6,
    },
  };

  buttonSelected = signal({
    allProducts: {
      online: false,
      app: false,
      delivery: false,
    },
    noProducts: {
      online: false,
      app: false,
      delivery: false,
    },
    expandAll: {
      online: false,
      app: false,
      delivery: false,
    },
    collapseAll: {
      online: false,
      app: false,
      delivery: false,
    },
  });

  newUser = signal(false);

  formGroupStammdatenControlNameValue: Signal<any>;
  formGroupStammdatenControlEmailValue: Signal<any>;

  formChangesDetailsLength = linkedSignal<[boolean, boolean], number>({
    source: () => [this.newUser(), this.wasUserDataChanged()],
    computation: ([newUserValue, wasUserDataChanged]) => {
      if (!newUserValue && wasUserDataChanged) {
        return untracked(() => this.formChangesDetails().length);
      }
      return 0;
    },
  });

  deliveryFormControlCfg = {};

  urlTutorialVideo1 = signal<SafeResourceUrl>(undefined);
  urlTutorialVideo2 = signal<SafeResourceUrl>(undefined);

  hasUserEndDtg = false;
  showUpload = signal(false);
  showVideos = signal(false);
  validatedUploadUsers = signal<UploadedUser[]>(undefined);
  validUsers = signal(0);
  showUploadPreview = signal(false);
  usersToImportCount = 0;
  importedUsersCount = 0;
  uploadError = signal(false);

  egpTypes: EgpTypes = {
    egp: 'WINTER',
    egpsummer: 'SUMMER',
    egw: 'WINTER',
    egwsummer: 'SUMMER',
  };

  eopTypes = [
    'TWO_DAY_ALL',
    'TWO_DAY',
    'TWO_DAY_SUMMER',
    'FOUR_DAY_ALL',
    'FOUR_DAY',
    'FOUR_DAY_SUMMER',
    'SIX_DAY_ALL',
    'SIX_DAY',
    'SIX_DAY_SUMMER',
    'EIGHT_DAY_ALL',
    'EIGHT_DAY',
    'EIGHT_DAY_SUMMER',
    'FIFTEEN_DAY_ALL',
    'FIFTEEN_DAY',
    'FIFTEEN_DAY_SUMMER',
  ];

  eopTypesWithDelivery = [
    'TWO_DAY_ALL',
    'TWO_DAY',
    'TWO_DAY_SUMMER',
    'FOUR_DAY_ALL',
    'FOUR_DAY',
    'FOUR_DAY_SUMMER',
    'SIX_DAY_ALL',
    'SIX_DAY',
    'SIX_DAY_SUMMER',
    'EIGHT_DAY_ALL',
    'EIGHT_DAY',
    'EIGHT_DAY_SUMMER',
  ];

  egwsummerThresholdOptions = [1, 2, 3, 4];

  userList = signal([]);

  tableData = signal<any[]>(undefined); // sliced userList data initialized with initTableData()
  pageSize = 10;
  pageSizeOptions = [5, 10, 20, 50];

  private subscription: Subscription;

  isSaving = signal(false);

  contractStartDtg: Moment;
  contractExpiryDtg: Moment;

  oldUserData;
  wasUserDataChanged = signal(false);
  changeDetails = signal([]);

  isEditingUserName = signal(false);

  isDeliveryActive = false;

  hasUserAdminPermissions = computed(() => {
    return this.auth.hasUserAdminPermissions();
  });

  isMaster = computed(() => {
    return this.auth.isMaster();
  });

  constructor() {
    this.userList.set([...this.userService.userList()]);
    this.urlTutorialVideo1.set(
      this.sanitizer.bypassSecurityTrustResourceUrl(
        'https://www.youtube.com/embed/kdwginpSbdw'
      )
    );
    this.urlTutorialVideo2.set(
      this.sanitizer.bypassSecurityTrustResourceUrl(
        'https://www.youtube.com/embed/fEfWLF5SdLg'
      )
    );
    this.userService.listenToShowAllProductsSelectedChanges();
  }

  add(event: AddEvent): void {
    const targetType = event.targetType as DeliveryTargetType;
    const normalizedValue = this.deliveryTargetService.normalizeTargetValue(
      targetType,
      event.event.value || ''
    );

    if (
      normalizedValue &&
      this.userService.deliveryTargetControl[targetType].valid &&
      this.deliveryTargetService.shouldAddTarget(
        this.deliveryTargets(),
        targetType,
        normalizedValue
      )
    ) {
      this.deliveryTargets.update(deliveryTargets =>
        this.deliveryTargetService.addTarget(
          deliveryTargets,
          targetType,
          normalizedValue
        )
      );

      this.onChangeContingent(targetType, true);
      this.wasUserDataChanged.set(true);
    }

    event.event.chipInput!.clear();
    this.userService.deliveryTargetControl[targetType].setValue(null);
  }

  remove(event: RemoveEvent): void {
    const targetType = event.targetType as DeliveryTargetType;
    const targetIndex = this.deliveryTargets()[targetType].indexOf(
      event.address
    );

    if (targetIndex < 0) {
      return;
    }

    const productPositions =
      this.deliveryTargetService.getProductAddressPositions(
        this.userService.userData(),
        targetType,
        event.address
      );

    const formControlPositions =
      this.deliveryTargetService.getFormControlAddressPositions(
        this.userService.formGroupDelivery,
        targetType,
        event.address
      );

    if (productPositions.length > 0 || formControlPositions.length > 0) {
      const dialogRef = this.dialog.open(DialogRemoveUserComponent, {
        data: {
          toRemove: 'address',
          targetType,
          address: event.address,
        },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.deliveryTargetService.removeTargetFromProducts(
            targetType,
            productPositions
          );
          this.deliveryTargetService.removeTargetFromFormControls(
            formControlPositions
          );
          this.removeTargetFromList(targetType, targetIndex);
        }
      });

      return;
    }

    this.removeTargetFromList(targetType, targetIndex);
  }

  removeTargetFromList(targetType: DeliveryTargetType, index: number): void {
    this.deliveryTargets.update(deliveryTargets =>
      this.deliveryTargetService.removeTarget(
        deliveryTargets,
        targetType,
        index
      )
    );

    this.onChangeContingent(targetType, false);
    this.wasUserDataChanged.set(true);
  }

  ngOnInit(): void {
    // this.oldUserData = {...this.userService.userData()};
    // this.oldUserData = Object.assign({}, this.userService.userData());
    this.subscription = this.userService.obsUserList.subscribe(item => {
      this.userList.set([...item]);
      this.initTableData();
    });

    this.userService.formGroupStammdaten = this._formBuilder.group({});
    this.userService.formGroupWebsite = this._formBuilder.group({});
    this.userService.formGroupDelivery = this._formBuilder.group({});
    this.userService.formGroupApp = this._formBuilder.group({});
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  destroyUserEditData(): void {
    this.userService.userData.set({});
    this.userService.editView.set(false);
    this.newUser.set(false);
    this.userService.formGroupStammdaten = null;
    this.formGroupStammdatenControlNameValue = null;
    this.formGroupStammdatenControlEmailValue = null;
    this.userService.formGroupWebsite = null;
    this.userService.formGroupApp = null;
    this.userService.formGroupDelivery = null;
    this.deliveryFormControlCfg = {};
    this.userService.showAllProductsSelected.update(
      showAllProductsSelected => ({
        ...showAllProductsSelected,
        online: true,
        app: true,
      })
    );
    this.wasUserDataChanged.set(false);
    this.isEditingUserName.set(false);
    this.deliveryTargets.set({
      ...this.deliveryTargetService.emptyDeliveryTargets,
    });
    this.userService.deliveryTargetControl.email.reset(null);
    this.userService.deliveryTargetControl.sms.reset(null);
    this.userService.deliveryTargetControl.fax.reset(null);

    this.egwgmaSmsPause = {
      isPause: false,
      start: 22,
      end: 6,
    };
    this.egwsummerConfig = {
      smsPause: {
        isPause: false,
        start: 22,
        end: 6,
      },
    };
    this.buttonSelected.set({
      allProducts: {
        online: false,
        app: false,
        delivery: false,
      },
      noProducts: {
        online: false,
        app: false,
        delivery: false,
      },
      expandAll: {
        online: false,
        app: false,
        delivery: false,
      },
      collapseAll: {
        online: false,
        app: false,
        delivery: false,
      },
    });
    this.isDeliveryActive = false;
  }

  initFormGroupChangedEvent(): void {
    this.userService.formGroupStammdaten.valueChanges.subscribe(selectedValue =>
      this.wasUserDataChanged.set(true)
    );
    this.userService.formGroupWebsite.valueChanges.subscribe(selectedValue =>
      this.wasUserDataChanged.set(true)
    );
    this.userService.formGroupDelivery.valueChanges.subscribe(selectedValue =>
      this.wasUserDataChanged.set(true)
    );
    this.userService.formGroupApp.valueChanges.subscribe(selectedValue =>
      this.wasUserDataChanged.set(true)
    );
  }

  onUserEdit(userDataFromList): void {
    // this.oldUserData = {...userData};
    this.userService.userData.set({ ...cloneDeep(userDataFromList) });
    this.userService.editView.set(true);
    this.newUser.set(false);

    for (const section of ['online', 'delivery', 'app']) {
      this.initSectionProducts(
        section,
        section !== 'delivery' && section !== 'app'
      );
    }
    this.oldUserData = cloneDeep(this.userService.userData());
    const userData = this.userService.userData();

    const EndDateValidator1: ValidatorFn = (fg: UntypedFormGroup) => {
      const endless = fg.get('endless').value;
      const dateTo = fg.get('dateTo').value;
      const dateFrom = fg.get('dateFrom').value;
      if (!endless) {
        if (
          !moment(dateTo).isValid() ||
          moment(dateTo).isBefore(moment(dateFrom))
        ) {
          return { endData: true };
        }
      }
      return null;
    };

    // stammdaten
    const dateStart = this.getUserDtg('start', userData);
    const dateEnd = this.getUserDtg('end', userData);
    this.hasUserEndDtg = dateEnd !== null;

    this.userService.formGroupStammdaten = this._formBuilder.group(
      {
        email: [
          userData['email'],
          [
            Validators.required,
            Validators.email,
            UsernameValidator.usernameValidator,
          ],
        ],
        name: [this.userService.getUserName(userData), Validators.required],
        admin: [this.userService.isUserAdmin(userData)],
        active: [this.userService.isUserActive(userData)],
        dateFrom: [
          dateStart,
          [Validators.required, DateValidator.dateValidator],
        ],
        dateTo: [dateEnd],
        endless: [!this.hasUserEndDtg],
        autoDelivery: [false],
      },
      { validators: [EndDateValidator.groupValidator] }
    );
    this.formGroupStammdatenControlNameValue = toSignal(
      this.userService.formGroupStammdaten.controls['name'].valueChanges,
      {
        initialValue:
          this.userService.formGroupStammdaten.controls['name'].value,
        injector: this.injector,
      }
    );
    this.formGroupStammdatenControlEmailValue = toSignal(
      this.userService.formGroupStammdaten.controls['email'].valueChanges,
      {
        initialValue:
          this.userService.formGroupStammdaten.controls['email'].value,
        injector: this.injector,
      }
    );

    // website
    const contingents = this.contingents();
    const dateOnlineStart = this.getSectionDtg('online', 'start', userData);
    const dateOnlineEnd = this.getSectionDtg('online', 'end', userData);
    const isOnlineActive = this.userService.isUserActive(userData, 'online');
    this.userService.formGroupWebsite = this._formBuilder.group(
      {
        active: {
          value: isOnlineActive,
          disabled:
            !isOnlineActive &&
            this.userService.usedContingents()['online'] >=
              contingents['online'],
        },
        dateFrom: [
          dateOnlineStart,
          [Validators.required, DateValidator.dateValidator],
        ],
        dateTo: [dateOnlineEnd],
        endless: [dateOnlineEnd == null],
      },
      { validators: [EndDateValidator.groupValidator] }
    );
    // app
    const dateAppStart = this.getSectionDtg('app', 'start', userData);
    const dateAppEnd = this.getSectionDtg('app', 'end', userData);
    const isAppActive = this.userService.isUserActive(userData, 'app');
    this.userService.formGroupApp = this._formBuilder.group(
      {
        active: {
          value: isAppActive,
          disabled:
            !isAppActive &&
            this.userService.usedContingents()['app'] >= contingents['app'],
        },
        dateFrom: [
          dateAppStart,
          [Validators.required, DateValidator.dateValidator],
        ],
        dateTo: [dateAppEnd],
        endless: [dateAppEnd == null],
      },
      { validators: [EndDateValidator.groupValidator] }
    );
    // delivery
    this.deliveryTargetsFromUserData();
    this.smsPauseFromUserData();
    const dateDeliveryStart = this.getSectionDtg('delivery', 'start', userData);
    const dateDeliveryEnd = this.getSectionDtg('delivery', 'end', userData);
    this.isDeliveryActive = this.userService.isUserActive(userData, 'delivery');
    this.userService.formGroupDelivery = this._formBuilder.group(
      {
        active: this.isDeliveryActive,
        dateFrom: [dateDeliveryStart, DateValidator.dateValidator],
        dateTo: [dateDeliveryEnd],
        endless: [dateDeliveryEnd == null],
        egwsummer_isSmsPause: this.egwsummerConfig.smsPause.isPause,
        egwsummer_smsPauseStart: [
          this.egwsummerConfig.smsPause.start,
          [Validators.min(0), Validators.max(23)],
        ],
        egwsummer_smsPauseEnd: [
          this.egwsummerConfig.smsPause.end,
          [Validators.min(0), Validators.max(23)],
        ],
        egwgma_isSmsPause: this.egwgmaSmsPause.isPause,
        egwgma_smsPauseStart: [
          this.egwgmaSmsPause.start,
          [Validators.min(0), Validators.max(23)],
        ],
        egwgma_smsPauseEnd: [
          this.egwgmaSmsPause.end,
          [Validators.min(0), Validators.max(23)],
        ],
      },
      { validators: [EndDateValidator.groupValidator] }
    );

    this.addDeliveryFormControls();
    this.initFormGroupChangedEvent();
  }

  onUserAdd(): void {
    this.oldUserData = {};
    const today = moment().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    const dtgEndless = this.userService.getEndlessDtg();
    const dtgFormat = 'YYYY-MM-DDT00:00:00Z';

    this.userService.editView.set(true);
    this.newUser.set(true);
    /*Stammdaten Form*/
    this.userService.formGroupStammdaten = this._formBuilder.group(
      {
        email: [
          '',
          {
            validators: [Validators.required, Validators.email],
            asyncValidators: [
              UsernameValidator.usernameValidator(this.userService),
            ],
          },
        ],
        name: ['', Validators.required],
        admin: [false],
        active: [true],
        dateFrom: [
          today.toISOString(),
          [Validators.required, DateValidator.dateValidator],
        ],
        dateTo: [''],
        endless: [true],
        autoDelivery: [false],
      },
      { validators: [EndDateValidator.groupValidator] }
    );
    this.formGroupStammdatenControlNameValue = toSignal(
      this.userService.formGroupStammdaten.controls['name'].valueChanges,
      {
        initialValue:
          this.userService.formGroupStammdaten.controls['name'].value,
        injector: this.injector,
      }
    );
    this.formGroupStammdatenControlEmailValue = toSignal(
      this.userService.formGroupStammdaten.controls['email'].valueChanges,
      {
        initialValue:
          this.userService.formGroupStammdaten.controls['email'].value,
        injector: this.injector,
      }
    );

    const contingents = this.contingents();
    const isOnlineContingentAvailable =
      this.userService.usedContingents()['online'] < contingents['online'];
    /*Website Form*/
    this.userService.formGroupWebsite = this._formBuilder.group(
      {
        active: {
          value: isOnlineContingentAvailable,
          disabled:
            this.userService.usedContingents()['online'] >=
            contingents['online'],
        },
        dateFrom: [today.toISOString(), DateValidator.dateValidator],
        dateTo: [''],
        endless: [true],
      },
      { validators: [EndDateValidator.groupValidator] }
    );
    /*App Form*/
    this.userService.formGroupApp = this._formBuilder.group(
      {
        active: {
          value: false,
          disabled:
            this.userService.usedContingents()['app'] >= contingents['app'],
        },
        dateFrom: [today.toISOString(), DateValidator.dateValidator],
        dateTo: [''],
        endless: [true],
      },
      { validators: [EndDateValidator.groupValidator] }
    );

    this.userService.formGroupDelivery = this._formBuilder.group(
      {
        active: [false],
        dateFrom: [today.toISOString(), DateValidator.dateValidator],
        dateTo: [''],
        endless: [true],
        egwsummer_isSmsPause: this.egwsummerConfig.smsPause.isPause,
        egwsummer_smsPauseStart: [
          this.egwsummerConfig.smsPause.start,
          [Validators.min(0), Validators.max(23)],
        ],
        egwsummer_smsPauseEnd: [
          this.egwsummerConfig.smsPause.end,
          [Validators.min(0), Validators.max(23)],
        ],
        egwgma_isSmsPause: this.egwgmaSmsPause.isPause,
        egwgma_smsPauseStart: [
          this.egwgmaSmsPause.start,
          [Validators.min(0), Validators.max(23)],
        ],
        egwgma_smsPauseEnd: [
          this.egwgmaSmsPause.end,
          [Validators.min(0), Validators.max(23)],
        ],
      },
      { validators: [EndDateValidator.groupValidator] }
    );

    this.userService.userData.set({
      customerId: this.userService.customerDocument().customerId,
      groups: ['EINSATZWETTER_USER'],
      email: '',
      name: '',
      metadata: {
        name: '',
        start: today.format(dtgFormat),
        end: null,
        active: true,
        online: {
          start: today.format(dtgFormat),
          end: dtgEndless,
          active: true,
          product: null,
        },
        delivery: {
          start: today.format(dtgFormat),
          end: dtgEndless,
          active: false,
          product: [],
        },
        app: {
          start: today.format(dtgFormat),
          end: dtgEndless,
          active: true,
          product: null,
        },
      },
    });
    this.addDeliveryFormControls();
  }

  addDeliveryFormControls(): void {
    const productList = this.productList();
    for (const productName of ['eop']) {
      for (const productType in productList[productName]) {
        for (const product of productList[productName][productType]) {
          const stationId = product.station.stationId;
          const formControlName = product.formControlName;
          const values = this.newUser()
            ? []
            : this.userProductDeliveryValues(product);
          this.userService.formGroupDelivery.addControl(
            formControlName,
            new UntypedFormControl(values)
          );
          this.deliveryFormControlCfg[formControlName] = {
            productId: product.id,
            stationId,
          };
        }
      }
    }
    for (const productName of ['epp']) {
      for (const parameterset of this.userService.eppParametersets()) {
        for (const resolution of this.userService.eppResolutions()) {
          const res = resolution.days + '_' + resolution.hourly;
          for (const product of productList[productName][parameterset][res]) {
            const stationId = product.station.stationId;
            const formControlName = product.formControlName;
            const values = this.newUser()
              ? []
              : this.userProductDeliveryValues(product);
            this.userService.formGroupDelivery.addControl(
              formControlName,
              new UntypedFormControl(values)
            );
            this.deliveryFormControlCfg[formControlName] = {
              productId: product.id,
              stationId,
              parameterset,
              resolution,
            };
          }
        }
      }
    }
    for (const productName of ['egwgma']) {
      for (const product of productList[productName]) {
        const stationId = product.station.stationId;
        const formControlName = product.formControlName;
        const values = this.newUser()
          ? []
          : this.userProductDeliveryValues(product);
        this.userService.formGroupDelivery.addControl(
          formControlName,
          new UntypedFormControl(values)
        );
        this.deliveryFormControlCfg[formControlName] = {
          productId: product.id,
          stationId,
        };
      }
    }

    for (const productName of ['egwsummer']) {
      for (const product of productList[productName]) {
        const formControlName = product.formControlName;
        const infoFormControlName = product.infoFormControlName;
        const nowcastFormControlName = product.nowcastFormControlName;
        const heavyRainWarningFormControlName =
          product.heavyRainWarningFormControlName;
        const precipitationWarningFormControlName =
          product.precipitationWarningFormControlName;
        const windWarningFormControlName = product.windWarningFormControlName;

        const values = this.newUser()
          ? []
          : this.userProductDeliveryValues(product);
        this.userService.formGroupDelivery.addControl(
          formControlName,
          new UntypedFormControl(values)
        );

        const infoValue = this.userProductDeliveryValueBySection(
          product,
          'info'
        );
        this.userService.formGroupDelivery.addControl(
          infoFormControlName,
          new UntypedFormControl(isNil(infoValue) ? true : infoValue)
        );

        const nowcastValue = this.userProductDeliveryValueBySection(
          product,
          'nowcast'
        );
        this.userService.formGroupDelivery.addControl(
          nowcastFormControlName,
          new UntypedFormControl(nowcastValue === undefined ? 1 : nowcastValue)
        );

        const heavyRainWarningValue = this.userProductDeliveryValueBySection(
          product,
          'heavyRainWarning'
        );
        this.userService.formGroupDelivery.addControl(
          heavyRainWarningFormControlName,
          new UntypedFormControl(
            heavyRainWarningValue === undefined ? 1 : heavyRainWarningValue
          )
        );

        const precipitationWarningValue =
          this.userProductDeliveryValueBySection(
            product,
            'precipitationWarning'
          );
        this.userService.formGroupDelivery.addControl(
          precipitationWarningFormControlName,
          new UntypedFormControl(
            precipitationWarningValue === undefined
              ? 1
              : precipitationWarningValue
          )
        );

        const windWarningValue = this.userProductDeliveryValueBySection(
          product,
          'windWarning'
        );
        this.userService.formGroupDelivery.addControl(
          windWarningFormControlName,
          new UntypedFormControl(
            windWarningValue === undefined ? 1 : windWarningValue
          )
        );

        this.deliveryFormControlCfg[formControlName] = {
          productId: product.id,
          info: infoFormControlName,
          nowcast: nowcastFormControlName,
          heavyRain: heavyRainWarningFormControlName,
          precipitationWarning: precipitationWarningFormControlName,
          windWarning: windWarningFormControlName,
        };
      }
    }

    for (const productName of [
      'egp',
      'egpsummer',
      'egw',
      // 'egwgma',
    ]) {
      for (const product of productList[productName]) {
        const formControlName = product.formControlName;
        const values = this.newUser()
          ? []
          : this.userProductDeliveryValues(product);
        this.userService.formGroupDelivery.addControl(
          formControlName,
          new UntypedFormControl(values)
        );
        this.deliveryFormControlCfg[formControlName] = {
          productId: product.id,
        };
      }
    }
  }

  initSectionProducts(section: string, selectAll = false): void {
    this.userProductService.initSectionProducts(section, selectAll);
  }

  getUserDtg(whichDate: string, userData): string {
    let dateString = '';
    if (userData.hasOwnProperty('metadata') && userData['metadata'] !== null) {
      if (
        userData['metadata'].hasOwnProperty(whichDate) &&
        userData['metadata'][whichDate] !== null
      ) {
        dateString = userData['metadata'][whichDate];
        if (dateString === 'Invalid date') {
          return null;
        }
        if (whichDate === 'end') {
          if (!moment(userData['metadata'][whichDate]).isValid()) {
            return null;
          }
          if (
            moment(userData['metadata'][whichDate]).isAfter(
              moment().add(20, 'years')
            )
          ) {
            return null;
          }
        }
        return moment(userData['metadata'][whichDate]).isValid()
          ? moment(userData['metadata'][whichDate]).toISOString()
          : null;
      }
    }
    return whichDate === 'start' ? moment().toISOString() : null;
  }

  getSectionDtg(section: string, whichDate: string, userData): string {
    if (userData.hasOwnProperty('metadata') && userData['metadata'] !== null) {
      if (
        userData['metadata'].hasOwnProperty(section) &&
        userData['metadata'][section] !== null
      ) {
        if (userData['metadata'][section].hasOwnProperty(whichDate)) {
          if (userData['metadata'][section][whichDate] !== null) {
            if (
              whichDate === 'end' &&
              (!moment(userData['metadata'][section][whichDate]).isValid() ||
                moment(userData['metadata'][section][whichDate]).isAfter(
                  moment().add(20, 'years')
                ))
            ) {
              return null;
            }
          }
        } else if (whichDate === 'end') {
          return null;
        }
        return moment(userData['metadata'][section][whichDate]).isValid()
          ? moment(userData['metadata'][section][whichDate]).toISOString()
          : null;
      }
    }
    return whichDate === 'start' ? moment().toISOString() : null;
  }

  deliveryTargetsFromUserData(): void {
    const fallbackEmail =
      this.userService.formGroupStammdaten.controls['email'].value;

    this.deliveryTargets.set(
      this.deliveryTargetService.createDeliveryTargetsFromUserData(
        this.userService.userData(),
        fallbackEmail
      )
    );
  }

  smsPauseFromUserData(): void {
    if (
      this.userService.userData()['metadata']['delivery']['product'] === null
    ) {
      return;
    }
    for (const deliveryProduct of this.userService.userData()['metadata'][
      'delivery'
    ]['product']) {
      if (this.isGmaAlarmProduct(deliveryProduct.id)) {
        if (!isNil(deliveryProduct['pauseStart'])) {
          this.egwgmaSmsPause.start = deliveryProduct['pauseStart'];
          this.egwgmaSmsPause.isPause = true;
        }
        if (!isNil(deliveryProduct['pauseEnd'])) {
          this.egwgmaSmsPause.end = deliveryProduct['pauseEnd'];
          this.egwgmaSmsPause.isPause = true;
        }
        if (this.egwgmaSmsPause.start === this.egwgmaSmsPause.end) {
          this.egwgmaSmsPause.isPause = false;
        }
      } else if (this.isSummerWarningProduct(deliveryProduct.id)) {
        if (!isNil(deliveryProduct['pauseStart'])) {
          this.egwsummerConfig.smsPause.start = deliveryProduct['pauseStart'];
          this.egwsummerConfig.smsPause.isPause = true;
        }
        if (!isNil(deliveryProduct['pauseEnd'])) {
          this.egwsummerConfig.smsPause.end = deliveryProduct['pauseEnd'];
          this.egwsummerConfig.smsPause.isPause = true;
        }
        if (
          this.egwsummerConfig.smsPause.start ===
          this.egwsummerConfig.smsPause.end
        ) {
          this.egwsummerConfig.smsPause.isPause = false;
        }
      }
    }
  }

  userProductDeliveryValues(product): string[] {
    return this.userProductService.userProductDeliveryValues(
      this.userService.userData(),
      product
    );
  }

  userProductDeliveryValueBySection(
    product,
    section: string,
    isThreshold = true
  ) {
    return this.userProductService.userProductDeliveryValueBySection(
      this.userService.userData(),
      product,
      section,
      isThreshold
    );
  }

  onSummarizeUser(): void {
    if (this.newUser()) {
      this.onSubmit();
    }
    const details = this.formChangesDetails();
    const dialogRef = this.dialog.open(DialogSummarizeUserComponent, {
      data: details,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.onSubmit();
      }
    });
  }

  onDeleteUser(userData): void {
    const dialogRef = this.dialog.open(DialogRemoveUserComponent, {
      data: {
        toRemove: 'user',
        userData,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.userService.deleteUser(userData).subscribe(
          data => {
            this.userService.getUsers();
          },
          err => console.error(err)
        );
      }
    });
  }

  onCheckEmailToAddToDeliveryTargets(): void {
    if (this.userService.formGroupStammdaten.controls['email'].valid) {
      const email =
        this.userService.formGroupStammdaten.controls['email'].value;
      if (!this.deliveryTargets().email.includes(email)) {
        this.deliveryTargets.update(deliveryTargets => {
          return {
            ...deliveryTargets,
            email: [...deliveryTargets.email, email],
          };
        });
      }
      this.wasUserDataChanged.set(true);
    }
  }

  onAddEmailToDeliveryTargets(): void {
    if (
      this.newUser() &&
      this.userService.formGroupStammdaten.controls['email'].valid
    ) {
      if (this.deliveryTargets().email.length === 0) {
        this.deliveryTargets.update(deliveryTargets => {
          return {
            ...deliveryTargets,
            email: [
              ...deliveryTargets.email,
              this.userService.formGroupStammdaten.controls['email'].value,
            ],
          };
        });
      }
      this.wasUserDataChanged.set(true);
    }
  }

  onSubmit(): void {
    this.isSaving.set(true);
    this.userService.loadingUserList.set(true);
    const dtgFormat = 'YYYY-MM-DDT00:00:00';
    // const userData = this.newUser() ? {} : this.userService.userData();
    const userData = cloneDeep(this.userService.userData());
    const expressWithAutoDelivery =
      this.newUser() &&
      this.userService.formGroupStammdaten.controls['autoDelivery'].value;

    userData['customerId'] = this.userService.customerDocument().customerId;
    userData['email'] =
      this.userService.formGroupStammdaten.controls['email'].value;
    userData['groups'] = ['EINSATZWETTER_USER'];
    if (this.userService.formGroupStammdaten.controls['admin'].value) {
      userData['groups'].push('EINSATZWETTER_USER_ADMIN');
    }
    if (!userData.hasOwnProperty('metadata') || userData['metadata'] === null) {
      userData['metadata'] = {};
    }
    userData['metadata']['name'] =
      this.userService.formGroupStammdaten.controls['name'].value;
    userData['metadata']['active'] =
      this.userService.formGroupStammdaten.controls['active'].value;
    userData['metadata']['start'] =
      moment(
        this.userService.formGroupStammdaten.controls['dateFrom'].value
      ).format(dtgFormat) + 'Z';
    //    userData['metadata']['end'] = moment(null).format(dtgFormat);
    userData['metadata']['end'] =
      this.userService.formGroupStammdaten.controls['endless'].value ||
      this.userService.formGroupStammdaten.controls['dateTo'].value === null
        ? this.userService.getEndlessDtg()
        : moment(
            this.userService.formGroupStammdaten.controls['dateTo'].value
          ).format(dtgFormat) + 'Z';
    //      userData['metadata']['end'] = this.userService.formGroupStammdaten.controls['endless'].value || this.userService.formGroupStammdaten.controls['dateTo'].value === null
    //      ? this.userService.getEndlessDtg() : moment(this.userService.formGroupStammdaten.controls['dateTo'].value).format(dtgFormat);

    if (
      !userData['metadata'].hasOwnProperty('online') ||
      userData['metadata']['online'] === null
    ) {
      userData['metadata']['online'] = {};
    }
    userData['metadata']['online']['active'] =
      this.userService.formGroupWebsite.controls['active'].value;
    if (this.userService.formGroupWebsite.controls['dateFrom'].value !== '') {
      userData['metadata']['online']['start'] =
        moment(
          this.userService.formGroupWebsite.controls['dateFrom'].value
        ).format(dtgFormat) + 'Z';
      userData['metadata']['online']['end'] =
        this.userService.formGroupWebsite.controls['endless'].value ||
        this.userService.formGroupWebsite.controls['dateTo'].value === null
          ? this.userService.getEndlessDtg()
          : moment(
              this.userService.formGroupWebsite.controls['dateTo'].value
            ).format(dtgFormat) + 'Z';
    }
    if (!userData['metadata']['online'].hasOwnProperty('product')) {
      userData['metadata']['online']['product'] = null;
    }

    if (
      !userData['metadata'].hasOwnProperty('app') ||
      userData['metadata']['app'] === null
    ) {
      userData['metadata']['app'] = {};
    }
    userData['metadata']['app']['active'] =
      this.userService.formGroupApp.controls['active'].value;
    if (this.userService.formGroupApp.controls['dateFrom'].value !== '') {
      userData['metadata']['app']['start'] =
        moment(this.userService.formGroupApp.controls['dateFrom'].value).format(
          dtgFormat
        ) + 'Z';
      userData['metadata']['app']['end'] =
        this.userService.formGroupApp.controls['endless'].value ||
        this.userService.formGroupApp.controls['dateTo'].value === null
          ? this.userService.getEndlessDtg()
          : moment(
              this.userService.formGroupApp.controls['dateTo'].value
            ).format(dtgFormat) + 'Z';
    }
    if (!userData['metadata']['app'].hasOwnProperty('product')) {
      userData['metadata']['app']['product'] = null;
    }

    // Delivery
    if (expressWithAutoDelivery) {
      this.userService.formGroupDelivery.controls['active'].setValue(true);
      this.selectEachProduct('delivery');
      userData['metadata']['delivery']['product'] = cloneDeep(
        this.userService.userData()['metadata']['delivery']['product']
      );
    }
    if (
      !userData['metadata'].hasOwnProperty('delivery') ||
      userData['metadata']['delivery'] === null
    ) {
      userData['metadata']['delivery'] = {};
    }
    if (!userData['metadata']['delivery'].hasOwnProperty('target')) {
      userData['metadata']['delivery']['target'] = {
        email: [],
        sms: [],
        fax: [],
      };
    }
    for (const targetType of ['email', 'sms', 'fax']) {
      // userData['metadata']['delivery']['target'][targetType] = this.deliveryTargets[targetType];
      userData['metadata']['delivery']['target'][targetType] = [];
      for (const target of this.deliveryTargets()[targetType]) {
        let verifiedTarget = target;
        if (targetType !== 'email') {
          verifiedTarget = verifyPhoneNumber(target);
        }
        if (verifiedTarget !== null) {
          userData['metadata']['delivery']['target'][targetType].push(
            verifiedTarget
          );
        }
      }
    }

    userData['metadata']['delivery']['active'] =
      this.userService.formGroupDelivery.controls['active'].value;
    if (this.userService.formGroupDelivery.controls['dateFrom'].value !== '') {
      userData['metadata']['delivery']['start'] =
        moment(
          this.userService.formGroupDelivery.controls['dateFrom'].value
        ).format(dtgFormat) + 'Z';
      userData['metadata']['delivery']['end'] =
        this.userService.formGroupDelivery.controls['endless'].value ||
        this.userService.formGroupDelivery.controls['dateTo'].value === null
          ? this.userService.getEndlessDtg()
          : moment(
              this.userService.formGroupDelivery.controls['dateTo'].value
            ).format(dtgFormat) + 'Z';
    }
    if (!userData['metadata']['delivery'].hasOwnProperty('product')) {
      userData['metadata']['delivery']['product'] = [];
    } else {
      userData['metadata']['delivery']['product'] = this.updateDeliveryProduct(
        userData['metadata']['delivery']
      );
    }

    if (userData.hasOwnProperty('name')) {
      delete userData['name'];
    }

    if (this.newUser()) {
      this.userService.createUser(userData).subscribe(
        data => {
          this.userService.getUsers();
          this.destroyUserEditData();
          this.isSaving.set(false);
          this._snackBar.open(
            'Der Benutzer wurde eingerichtet. Er wird automatisch per E-Mail benachrichtigt.',
            'OK',
            {
              duration: 20000,
              verticalPosition: 'top',
              panelClass: 'snackbar-info',
            }
          );
          //          this.userService.extractUserDataForProductList();
        },
        err => {
          const errMessage: string = err;
          this._snackBar.open(
            'Benutzer konnte nicht angelegt werden ' + errMessage,
            'OK',
            {
              duration: 20000,
              verticalPosition: 'top',
              panelClass: 'snackbar-error',
            }
          );
          this.isSaving.set(false);
        }
      );
    } else {
      this.userService.modifyUser(userData).subscribe(
        data => {
          this.userService.getUsers();
          this.destroyUserEditData();
          this.isSaving.set(false);
        },
        err => {
          console.error(err);
          this.isSaving.set(false);
        }
      );
    }
  }

  onAutoDelivery(event: MatCheckboxChange): void {
    if (event.checked) {
      if (!this.userService.formGroupDelivery.controls['active'].value) {
        this.userService.formGroupDelivery.controls['active'].setValue(true);
      }
    }
    this.wasUserDataChanged.set(true);
  }

  onStepperSelectionChanged(event: StepperSelectionEvent): void {
    if (
      this.newUser() &&
      event.selectedIndex === 2 &&
      this.userService.formGroupStammdaten.controls['autoDelivery'].value
    ) {
      this.onSelectButShowAllProducts('delivery');
    }
  }

  updateDeliveryProduct(deliveryData): Array<any> {
    return this.userProductService.updateDeliveryProduct({
      deliveryData,
      formGroupDelivery: this.userService.formGroupDelivery,
      deliveryFormControlCfg: this.deliveryFormControlCfg,
      productList: this.productList(),
      egwgmaSmsPause: this.egwgmaSmsPause,
      egwsummerConfig: this.egwsummerConfig,
      isGmaAlarmProduct: productId => this.isGmaAlarmProduct(productId),
      isSummerWarningProduct: productId =>
        this.isSummerWarningProduct(productId),
    });
  }

  userProductAlradyExist(product, allProducts: any[]): boolean {
    for (const dataset of allProducts) {
      if (product.productId === dataset.productId) {
        if (
          product.hasOwnProperty('stationId') &&
          product.stationId === dataset.stationId
        ) {
          if (product.hasOwnProperty('parameterset')) {
            if (product.parameterset === dataset.parameterset) {
              return true;
            }
          } else {
            return true;
          }
        }
      }
    }
    return false;
  }
  onChangePause(
    productType: 'egwgma' | 'egwsummer',
    section: string,
    event
  ): void {
    if (event.target.validity.valid) {
      if (productType === 'egwgma') {
        this.egwgmaSmsPause[section] = Number(event.target.value);
      }
      if (productType === 'egwsummer') {
        this.egwsummerConfig.smsPause[section] = Number(event.target.value);
      }
    }
  }

  onClickAllProducts(event: ClickSectionCheckbox): void {
    if (event.event.checked) {
      this.userService.userData.update(userData => {
        return {
          ...userData,
          ['metadata']: {
            ...userData['metadata'],
            [event.section]: {
              ...userData['metadata'][event.section],
              ['product']: null,
            },
          },
        };
      });
      this.userService.showAllProductsSelected.update(
        showAllProductsSelected => ({
          ...showAllProductsSelected,
          [event.section]: true,
        })
      );
    } else {
      this.userService.userData.update(userData => {
        return {
          ...userData,
          ['metadata']: {
            ...userData['metadata'],
            [event.section]: {
              ...userData['metadata'][event.section],
              ['product']: [],
            },
          },
        };
      });
      this.userService.showAllProductsSelected.update(
        showAllProductsSelected => ({
          ...showAllProductsSelected,
          [event.section]: false,
        })
      );
      this.buttonSelected.update(buttonSelected => {
        return {
          ...buttonSelected,
          noProducts: { ...buttonSelected.noProducts, [event.section]: true },
          allProducts: {
            ...buttonSelected.allProducts,
            [event.section]: false,
          },
        };
      });
    }
    this.wasUserDataChanged.set(true);
  }

  onSelectButShowAllProducts(section: string): void {
    this.userService.userData.update(userData => {
      return {
        ...userData,
        ['metadata']: {
          ...userData['metadata'],
          [section]: {
            ...userData['metadata'][section],
            ['product']: [],
          },
        },
      };
    });

    this.selectEachProduct(section);
    this.userService.showAllProductsSelected.update(
      showAllProductsSelected => ({
        ...showAllProductsSelected,
        [section]: false,
      })
    );
    if (section === 'delivery') {
      if (
        !this.userService.formGroupStammdaten.controls['autoDelivery'].value
      ) {
        this.userService.formGroupStammdaten.controls['autoDelivery'].setValue(
          true
        );
      }
    }
    this.buttonSelected.update(buttonSelected => {
      return {
        ...buttonSelected,
        noProducts: { ...buttonSelected.noProducts, [section]: false },
        allProducts: {
          ...buttonSelected.allProducts,
          [section]: true,
        },
      };
    });

    this.wasUserDataChanged.set(true);
  }

  onUnselectAllProducts(section: string): void {
    this.userService.userData.update(userData => {
      return {
        ...userData,
        ['metadata']: {
          ...userData['metadata'],
          [section]: {
            ...userData['metadata'][section],
            ['product']: [],
          },
        },
      };
    });
    this.userService.showAllProductsSelected.update(
      showAllProductsSelected => ({
        ...showAllProductsSelected,
        [section]: false,
      })
    );
    if (section === 'delivery') {
      this.userService.formGroupStammdaten.controls['autoDelivery'].setValue(
        false
      );
    }
    this.buttonSelected.update(buttonSelected => {
      return {
        ...buttonSelected,
        noProducts: { ...buttonSelected.noProducts, [section]: true },
        allProducts: {
          ...buttonSelected.allProducts,
          [section]: false,
        },
      };
    });
    this.wasUserDataChanged.set(true);
  }

  selectEachProduct(section: string, duringImport = false): void {
    this.userProductService.selectEachProduct({
      section,
      duringImport,
      productList: this.productList(),
      deliveryTargets: this.deliveryTargets(),
      deliveryFormControlCfg: this.deliveryFormControlCfg,
      formGroupDelivery: this.userService.formGroupDelivery,
      defaultDateFrom: this.defaultDateFrom(section, duringImport),
    });
  }

  productInUser(productId: number, userProducts: any[], options = {}): any {
    return this.userProductService.productInUser(
      productId,
      userProducts,
      options
    );
  }

  isStationInUserProduct(
    productId: number,
    stationId: string,
    userProducts: any[]
  ): boolean {
    return this.userProductService.isStationInUserProduct(
      productId,
      stationId,
      userProducts
    );
  }

  productIndex(productId: number, productList: any[]): number {
    return this.userProductService.productIndex(productId, productList);
  }

  newUserProduct(
    productName: string,
    section: string,
    productId: number,
    firstStationId: string = null,
    duringImport = false,
    options = {}
  ): object {
    return this.userProductService.newUserProduct(
      productName,
      section,
      productId,
      firstStationId,
      this.defaultDateFrom(section, duringImport),
      duringImport,
      this.deliveryTargets(),
      options
    );
  }

  defaultDateFrom(section: string, duringImport = false): any {
    return this.userProductService.getDefaultDateFrom(
      section,
      {
        website: this.userService.formGroupWebsite,
        delivery: this.userService.formGroupDelivery,
        app: this.userService.formGroupApp,
        masterData: this.userService.formGroupStammdaten,
      },
      duringImport
    );
  }

  onExpandAll(section: string): void {
    switch (section) {
      case 'online':
        this.usersTabWebsiteComponent().accordionEppOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionEopOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionEgpOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionEgwOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionSmwOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionRadarOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionMarwisOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionRouteOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionTaubenOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionAmpelOnline()?.openAll();
        this.usersTabWebsiteComponent().accordionTrendOnline()?.openAll();
        break;
      case 'app':
        this.usersTabAppComponent().accordionEppApp()?.openAll();
        this.usersTabAppComponent().accordionEopApp()?.openAll();
        this.usersTabAppComponent().accordionEgpApp()?.openAll();
        this.usersTabAppComponent().accordionEgwApp()?.openAll();
        this.usersTabAppComponent().accordionSmwApp()?.openAll();
        this.usersTabAppComponent().accordionRadarApp()?.openAll();
        break;
      case 'delivery':
        this.usersTabShippingComponent().accordionEopDelivery()?.openAll();
        this.usersTabShippingComponent().accordionEppDelivery()?.openAll();
        this.usersTabShippingComponent().accordionEgpDelivery()?.openAll();
        this.usersTabShippingComponent().accordionEgwDelivery()?.openAll();
        this.usersTabShippingComponent().accordionEgwGmaDelivery()?.openAll();
        break;
    }
    this.buttonSelected.update(buttonSelected => {
      return {
        ...buttonSelected,
        expandAll: { ...buttonSelected.expandAll, [section]: true },
        collapseAll: {
          ...buttonSelected.collapseAll,
          [section]: false,
        },
      };
    });
  }

  onCollapseAll(section: string): void {
    switch (section) {
      case 'online':
        this.usersTabWebsiteComponent().accordionEppOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionEopOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionEgpOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionEgwOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionSmwOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionRadarOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionMarwisOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionRouteOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionTaubenOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionAmpelOnline()?.closeAll();
        this.usersTabWebsiteComponent().accordionTrendOnline()?.closeAll();
        break;
      case 'app':
        this.usersTabAppComponent().accordionEppApp()?.closeAll();
        this.usersTabAppComponent().accordionEopApp()?.closeAll();
        this.usersTabAppComponent().accordionEgpApp()?.closeAll();
        this.usersTabAppComponent().accordionEgwApp()?.closeAll();
        this.usersTabAppComponent().accordionSmwApp()?.closeAll();
        this.usersTabAppComponent().accordionRadarApp()?.closeAll();
        break;
      case 'delivery':
        this.usersTabShippingComponent().accordionEopDelivery()?.closeAll();
        this.usersTabShippingComponent().accordionEppDelivery()?.closeAll();
        this.usersTabShippingComponent().accordionEgpDelivery()?.closeAll();
        this.usersTabShippingComponent().accordionEgwDelivery()?.closeAll();
        this.usersTabShippingComponent().accordionEgwGmaDelivery()?.closeAll();
        break;
    }
    this.buttonSelected.update(buttonSelected => {
      return {
        ...buttonSelected,
        expandAll: { ...buttonSelected.expandAll, [section]: false },
        collapseAll: {
          ...buttonSelected.collapseAll,
          [section]: true,
        },
      };
    });
  }

  eppProductsInList(): Array<any> {
    const productName = 'epp';
    const productList = this.productList();
    const parameterset = Object.keys(productList.epp)[0];
    if (parameterset && productList.epp.hasOwnProperty(parameterset)) {
      const res = Object.keys(productList.epp[parameterset])[0];
      if (
        productList.hasOwnProperty(productName) &&
        productList[productName].hasOwnProperty(parameterset) &&
        productList[productName][parameterset].hasOwnProperty(res)
      ) {
        return productList[productName][parameterset][res];
      }
    }
    return [];
  }

  onUserStationChange(event: UserStationChange): void {
    this.markProductSelectionChanged(event.section);

    this.userProductService.changeUserStationProduct({
      section: event.section,
      product: event.product,
      checked: event.event.checked,
      defaultDateFrom: moment().format('YYYY-MM-DDT00:00:00Z'),
    });

    this.wasUserDataChanged.set(true);
  }

  private markProductSelectionChanged(section: string): void {
    this.buttonSelected.update(buttonSelected => ({
      ...buttonSelected,
      allProducts: {
        ...buttonSelected.allProducts,
        [section]: false,
      },
      noProducts: {
        ...buttonSelected.noProducts,
        [section]: false,
      },
    }));
  }

  onSmsPauseChange(event: {
    checked: boolean;
    productType: 'egwgma' | 'egwsummer';
  }) {
    if (event.productType === 'egwgma') {
      this.egwgmaSmsPause.isPause = event.checked;
    }
    if (event.productType === 'egwsummer') {
      this.egwsummerConfig.smsPause.isPause = event.checked;
    }
  }

  onUserProductChange(event: UserStationChange): void {
    this.markProductSelectionChanged(event.section);

    this.userProductService.changeUserProduct({
      section: event.section,
      product: event.product,
      checked: event.event.checked,
      defaultDateFrom: moment().format('YYYY-MM-DDT00:00:00Z'),
    });

    this.wasUserDataChanged.set(true);
  }

  onDeliverySetActive(event: MatCheckboxChange): void {
    this.isDeliveryActive = event.checked;
    //    this.cd.detectChanges();
    if (event.checked) {
      // this.buttonSelected.update(buttonSelected => {
      //   return {
      //     ...buttonSelected,
      //     expandAll: {
      //       ...buttonSelected.allProducts,
      //       delivery: true,
      //     },
      //   };
      // });
      // this.cd.detectChanges();
      // this.onExpandAll('delivery');
      this.cd.detectChanges();
    }
  }

  onDeliveryChange(
    event: MatCheckboxChange,
    product,
    formControl: string
  ): void {
    this.userProductService.changeDeliveryProduct({
      checked: event.checked,
      product,
      formControlName: formControl,
      start: this.getSectionDtg(
        'delivery',
        'start',
        this.userService.userData()
      ),
      end: this.getSectionDtg('delivery', 'end', this.userService.userData()),
      egwgmaSmsPause: this.egwgmaSmsPause,
      egwsummerConfig: this.egwsummerConfig,
    });

    this.wasUserDataChanged.set(true);
  }

  onUsersUpload(): void {
    this.uploadError.set(false);

    const fileInput = this.fileInput().nativeElement;

    fileInput.onchange = () => {
      const files = fileInput.files;

      if (!files?.[0]) {
        return;
      }

      const reader: FileReader = new FileReader();

      reader.onloadend = () => {
        const csvContent = reader.result as string;
        const newUsers =
          this.userAdminImportService.readUploadedCsv(csvContent);

        this.userAdminImportService.verifyNewUsers(newUsers).then(users => {
          this.validatedUploadUsers.set([...users]);

          if (this.validatedUploadUsers().length > 0) {
            this.validUsers.set(
              this.validatedUploadUsers().filter(user => user.valid).length
            );
            this.showUploadPreview.set(true);
          } else {
            this.uploadError.set(true);
          }
        });
      };

      reader.readAsText(files[0]);
    };

    fileInput.click();
  }

  onStartImport(): void {
    this.usersToImportCount = 0;
    this.importedUsersCount = 0;
    for (const user of this.validatedUploadUsers()) {
      if (user.valid) {
        this.usersToImportCount++;
        const userData = this.userDataToImport(user);
        this.userService.createUser(userData).subscribe(
          data => {
            this.importedUsersCount++;
            if (this.importedUsersCount === this.usersToImportCount) {
              this.showUploadPreview.set(false);
              this.showUpload.set(false);
              this.userService.getUsers();
              this.destroyUserEditData();
            }
          },
          err => console.error(err)
        );
      }
    }
  }

  userDataToImport(user: UploadedUser): any {
    const canBecomeActive = this.getImportUserAvailability();
    const deliveryTarget =
      this.userAdminImportService.createImportUserDeliveryTargets(
        user,
        this.contingents(),
        this.userService.usedContingents()
      );

    this.deliveryTargets.set({
      email: deliveryTarget.email,
      sms: deliveryTarget.sms,
      fax: deliveryTarget.fax,
    });

    const userData = this.userAdminImportService.createUserDataToImport(
      user,
      canBecomeActive,
      deliveryTarget
    );

    this.userService.userData.set(userData);

    if (user.autodelivery) {
      this.selectEachProduct('delivery', true);
    }

    return this.userService.userData();
  }

  private getImportUserAvailability(): { online: boolean; app: boolean } {
    const contingents = this.contingents();
    const canBecomeActive = { online: false, app: false };

    for (const section of ['online', 'app']) {
      if (this.userService.usedContingents()[section] < contingents[section]) {
        canBecomeActive[section] = true;

        this.userService.usedContingents.update(usedContingents => ({
          ...usedContingents,
          [section]: usedContingents[section] + 1,
        }));
      }
    }

    return canBecomeActive;
  }

  onExpandedChanged(section: string): void {
    this.buttonSelected.update(buttonSelected => {
      return {
        ...buttonSelected,
        expandAll: { ...buttonSelected.expandAll, [section]: false },
        collapseAll: {
          ...buttonSelected.collapseAll,
          [section]: false,
        },
      };
    });
  }

  formChangesDetails(): Array<any> {
    const details = [];
    let detail;
    if (this.userService.formGroupStammdaten.touched) {
      detail = { name: 'STAMMDATEN', changedFields: [] };
      for (const control of Object.keys(
        this.userService.formGroupStammdaten.controls
      )) {
        if (this.userService.formGroupStammdaten.controls[control].touched) {
          detail.changedFields.push(control);
        }
      }
      if (detail.changedFields.length > 0) {
        details.push(detail);
      }
    }
    detail = { name: 'WEBSITE', changedFields: [] };
    if (this.userService.formGroupWebsite.touched) {
      for (const control of Object.keys(
        this.userService.formGroupWebsite.controls
      )) {
        if (this.userService.formGroupWebsite.controls[control].touched) {
          detail.changedFields.push(control);
        }
      }
    }
    if (this.areThereDifferencesInProducts('online')) {
      detail.changedFields.push('products');
    }
    if (detail.changedFields.length > 0) {
      details.push(detail);
    }
    detail = { name: 'DELIVERY', changedFields: [] };

    //    if (this.userService.formGroupDelivery.touched) {
    for (const control of Object.keys(
      this.userService.formGroupDelivery.controls
    )) {
      if (
        this.userService.formGroupDelivery.controls[control].touched &&
        detail.changedFields.indexOf('RECEIPIENT') < 0
      ) {
        detail.changedFields.push('RECEIPIENT');
      }
    }
    //    }

    if (this.areThereDifferencesInProducts('delivery')) {
      detail.changedFields.push('products');
    }
    if (this.areThereDifferencesInUserDataDeliveryTargets()) {
      detail.changedFields.push('targets');
    }
    if (detail.changedFields.length > 0) {
      details.push(detail);
    }
    detail = { name: 'APP', changedFields: [] };
    if (this.userService.formGroupApp.touched) {
      for (const control of Object.keys(
        this.userService.formGroupApp.controls
      )) {
        if (this.userService.formGroupApp.controls[control].touched) {
          detail.changedFields.push(control);
        }
      }
    }
    if (this.areThereDifferencesInProducts('app')) {
      detail.changedFields.push('products');
    }
    if (detail.changedFields.length > 0) {
      details.push(detail);
    }
    this.changeDetails.set([...details]);
    if (details.length === 0 && this.wasUserDataChanged()) {
      details.push({ name: 'DELIVERY', changedFields: [] });
    }
    return details;
  }

  areThereDifferencesInProducts(section: string): boolean {
    const hadProducts = this.hasNodes(this.oldUserData, [
      'metadata',
      section,
      'product',
    ]);
    const hasProducts = this.hasNodes(this.userService.userData(), [
      'metadata',
      section,
      'product',
    ]);
    if (!hadProducts && !hasProducts) {
      return false;
    }
    if (!hadProducts) {
      return true;
    }
    const oldProducts = this.oldUserData['metadata'][section]['product'];
    const newProducts =
      this.userService.userData()['metadata'][section]['product'];
    if (JSON.stringify(oldProducts) !== JSON.stringify(newProducts)) {
      return true;
    }
    return false;
  }

  areThereDifferencesInUserDataDeliveryTargets(): boolean {
    const hadTargets = this.hasNodes(this.oldUserData, [
      'metadata',
      'delivery',
      'target',
    ]);
    const hasTargets =
      this.deliveryTargets().email.length > 0 ||
      this.deliveryTargets().sms.length > 0 ||
      this.deliveryTargets().fax.length > 0;
    if (!hadTargets && !hasTargets) {
      return false;
    }
    if (!hadTargets) {
      return true;
    }
    const oldDeliveryTargets =
      this.oldUserData['metadata']['delivery']['target'];

    for (const targetType of ['email', 'fax', 'sms']) {
      const oldValues = this.getNodeValueAsString(
        oldDeliveryTargets,
        targetType
      );
      const newValues = JSON.stringify(this.deliveryTargets()[targetType]);
      if (oldValues !== newValues) {
        return true;
      }
    }
    return false;
  }

  getNodeValueAsString(object, node: string): string {
    if (object.hasOwnProperty(node)) {
      return JSON.stringify(object[node]);
    }
    return null;
  }

  hasNodes(object, nodes: string[]): boolean {
    let objectToSearch = object;
    for (const node of nodes) {
      if (objectToSearch !== null && objectToSearch.hasOwnProperty(node)) {
        objectToSearch = objectToSearch[node];
      } else {
        return false;
      }
    }
    return true;
  }

  onCancel(): void {
    if (!this.wasUserDataChanged()) {
      this.destroyUserEditData();
    } else {
      const dialogRef = this.dialog.open(DialogCancelUserComponent, {
        data: this.userService.formGroupStammdaten.controls['name'].value,
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.destroyUserEditData();
        }
      });
    }
  }

  onClickSectionCheckbox(event: ClickSectionCheckbox): void {
    if (event.event.checked) {
      this.userService.usedContingents.update(usedContingents => {
        return {
          ...usedContingents,
          [event.section]: usedContingents[event.section] + 1,
        };
      });
    } else {
      this.userService.usedContingents.update(usedContingents => {
        return {
          ...usedContingents,
          [event.section]: usedContingents[event.section] - 1,
        };
      });
    }
  }

  onChangeContingent(section: DeliveryTargetType, doAdd: boolean): void {
    this.userService.usedContingents.update(usedContingents => ({
      ...usedContingents,
      [section]: usedContingents[section] + (doAdd ? 1 : -1),
    }));
  }

  /* paginator methods - used for table to change shown data*/
  onChangePage(pageEvent: PageEvent): void {
    if (this.pageSize !== pageEvent.pageSize) {
      this.pageSize = pageEvent.pageSize;
    }
    this.tableData.set([
      ...this.userList().slice(
        pageEvent.pageIndex * pageEvent.pageSize,
        pageEvent.pageSize + pageEvent.pageIndex * pageEvent.pageSize
      ),
    ]);
  }

  initTableData(): void {
    this.tableData.set([...this.userList().slice(0, this.pageSize)]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initTableData();
  }

  filterUserList(): void {
    this.tableData.set([
      ...this.userList().filter(singleItem => {
        if (
          singleItem['email']
            .toLowerCase()
            .includes(this.searchString().toLowerCase()) ||
          singleItem['name']
            .toLowerCase()
            .includes(this.searchString().toLowerCase())
        ) {
          return true;
        }
      }),
    ]);

    if (!this.searchString()) {
      this.initTableData();
    }
  }

  private isSummerWarningProduct(productId: any) {
    return (
      this.userService.customerDocument()['egwSummerProducts'] ?? []
    ).some(product => product.id == productId);
  }

  private isGmaAlarmProduct(productId: any) {
    return (this.userService.customerDocument()['egwGmaProducts'] ?? []).some(
      product => product.id == productId
    );
  }
}
